"use client";
import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { GamePageModal } from "../GamePageModal";

const FullScreenMedia = dynamic(
  () =>
    import("../FullScreenMedia/FullScreenMedia").then((m) => m.FullScreenMedia),
  {
    ssr: false,
  },
);

interface ArtGalleryModalProps {
  gameData: GameData;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
}

export const ArtGalleryModal = ({
  gameData,
  isModalOpen,
  setIsModalOpen,
}: ArtGalleryModalProps) => {
  const { track } = useGamePageAnalytics();
  const [isImgOpen, setIsImgOpen] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  const initialViewedRef = useRef(false);

  useEffect(() => {
    if (!isModalOpen) {
      initialViewedRef.current = false;
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || initialViewedRef.current) return;
    initialViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        control: "modal",
        section: GamePageSection.ART_GALLERY,
      },
    });
  }, [isModalOpen, track]);

  const handleImageOpen = useCallback(
    (index: number) => {
      track({
        action: Action.PRESSED,
        extras: {
          control: "image",
          controlType: ControlType.IMAGE,
          section: GamePageSection.ART_GALLERY,
          controlLabel: index.toString(),
        },
      });

      setImgIndex(index);
      setIsImgOpen(true);
    },
    [track],
  );

  const handleCloseImg = useCallback(() => setIsImgOpen(false), []);

  return (
    <>
      <GamePageModal
        title="Art Gallery"
        gameTitle={gameData.details.name}
        gameIcon={gameData.details.logo_url}
        isModalOpen={isModalOpen}
        section={GamePageSection.ART_GALLERY}
        setIsModalOpen={setIsModalOpen}
        className="h-[95%] top-[52.5%] before:rounded-b-none [&>div]:rounded-b-none"
      >
        {/* Bento/Masonry Images Grid */}
        <div className="z-50 relative p-5 grid gap-5 overflow-y-auto auto-rows-[max-content] grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] md:auto-rows-[minmax(200px,240px)] lg:auto-rows-[minmax(220px,280px)] xl:auto-rows-[minmax(200px,320px)]">
          {gameData.gameContent?.art_gallery_urls?.map((imageUrl, index) => (
            <button
              type="button"
              key={imageUrl}
              className={cn(
                "relative overflow-hidden rounded-lg group cursor-pointer",
                index % 2 === 0 ? "md:row-span-2" : "md:row-span-1",
              )}
              onClick={() => handleImageOpen(index)}
              onMouseEnter={() => setImgIndex(index)}
              aria-label={`View ${gameData.details.name ?? "game"} art ${index + 1}`}
            >
              <Image
                src={imageUrl}
                alt={`${gameData.details.name ?? "Game"} Art ${index + 1}`}
                fill
                className="object-cover !relative transition-transform duration-300 ease-in-out group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              />
            </button>
          ))}
        </div>
      </GamePageModal>

      <FullScreenMedia
        mediaURL={gameData.gameContent?.art_gallery_urls?.[imgIndex] ?? ""}
        gameTitle={gameData.details.name}
        handleCloseMedia={handleCloseImg}
        isMediaOpen={isImgOpen}
        section={GamePageSection.ART_GALLERY}
      />
    </>
  );
};
